"use client"

import { ChevronIcon } from "@/app/icons/chevron"
import {
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
} from "@rarible_int/anaconda_ui_components"
import { useState } from "react"

type GachaFaqItem = {
  question: string
  answer: React.ReactNode
}

const gachaFaqItems: GachaFaqItem[] = [
  {
    question: "What is Rarible Gacha Station?",
    answer:
      "Gacha Station is where you crack open digital trading card packs, powered by Collector Crypt. Pull Pokemon, One Piece, Sports, Pop cards, each backed by a visible market value.",
  },
  {
    question: "Is Gacha Station powered by Collector Crypt?",
    answer:
      "Yes. Gacha Station runs on Collector Crypt's API, which powers the packs, its mechanics and pricing. Buyback offers, card redemption, and shipping are all handled and delivered directly by Collector Crypt.",
  },
  {
    question: "What series can I open right now?",
    answer:
      "Pokemon, One Piece, Sports, and Pop (a mix of Pokemon and One Piece Packs) Series.",
  },
  {
    question: "What blockchain does Gacha Station run on?",
    answer:
      "Solana chain. Gacha Station is available on Solana only right now.",
  },
  {
    question: "How do I create an account?",
    answer:
      "Log in with a Solana wallet (Phantom, Solflare, Backpack, or another compatible wallet), or log in with just your email if you'd rather skip the wallet setup.",
  },
  {
    question: "What do I need to open a pack?",
    answer:
      "USDC to purchase packs. If you don't have USDC yet, you can deposit directly on the site with a debit or credit card via onramp providers.",
  },
  {
    question: "What onramp providers does Gacha Station support?",
    answer: (
      <>
        Across, AlchemyPay, Banxa, Binance Connect, Blockchain.com, Coinbase,
        Guardarian, Kryptonim, Mercuryo, Moonpay, Paybis, Revolut, Robinhood,
        Router Protocol, Stripe Onramp, Swapped, Topper, and Transak.
        <br />
        <br />
        You won't pick the provider yourself, the system automatically assigns
        the one best suited to your region.
      </>
    ),
  },
  {
    question: "How long do I have to accept a buyback offer?",
    answer: (
      <>
        72 hours from when your result dialog appears. The price is locked in
        for that full window, it won't drop or change before you decide. You can
        always access any ongoing buyback offers in your{" "}
        <a
          href="/gacha/portfolio"
          className="text-brand underline underline-offset-2 hover:opacity-80"
        >
          Portfolio page
        </a>
        .
      </>
    ),
  },
  {
    question: "Can I gift a pack to someone else?",
    answer:
      "Yes, with Send as Gift. It only works between Solana wallets, and transfers can't be reversed, so double check the recipient's address before sending.",
  },
  {
    question: "Can I sell or trade the cards I pull?",
    answer:
      "Marketplace listing is coming soon. For now, Instant Buyback and individual buyback offers are how you get value back on cards you don't want to keep.",
  },
  {
    question: "Are the cards backed by real, physical cards?",
    answer:
      "Yes. Every card pulled through Gacha Station is backed by a real, physical trading card held in Collector Crypt's vault. The digital card you receive represents that physical card 1:1.",
  },
  {
    question: "Can I redeem my card for a physical version?",
    answer:
      "Shipping is coming soon, alongside Marketplace and a Points and Rewards program.",
  },
  {
    question: "How do I actually use Gacha Station, step by step?",
    answer: (
      <>
        Check out our full walkthrough{" "}
        <a
          href="https://x.com/rarible/status/2069044734790631526"
          target="_blank"
          rel="noreferrer"
          className="text-brand underline underline-offset-2 hover:opacity-80"
        >
          on X
        </a>
        .
      </>
    ),
  },
]

export function GachaFaqSection({ className }: { className?: string }) {
  return (
    <section className={cn("flex flex-col", className)}>
      <div className="mx-auto flex w-full max-w-[1192px] flex-col gap-5 px-4">
        <div className="flex flex-col gap-2">
          <div className="font-mono text-sm text-brand">FAQ</div>
          <h2 className="font-headline text-2xl leading-tight text-foreground">
            Gacha Station FAQ
          </h2>
        </div>

        <Card className="border-foreground/10 bg-foreground/[0.02] p-0">
          <div className="divide-y divide-foreground/10">
            {gachaFaqItems.map((item) => (
              <GachaFaqRow key={item.question} item={item} />
            ))}
          </div>
        </Card>
      </div>
    </section>
  )
}

function GachaFaqRow({ item }: { item: GachaFaqItem }) {
  const [open, setOpen] = useState(false)

  return (
    <Collapsible
      className="group/collapsible"
      open={open}
      onOpenChange={setOpen}
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left outline-none transition-colors hover:bg-foreground/[0.04] focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:px-5"
        >
          <span className="min-w-0 text-sm font-medium leading-snug text-foreground sm:text-base">
            {item.question}
          </span>
          <ChevronIcon className="size-4 shrink-0 text-foreground/70 transition-transform group-data-[state=open]/collapsible:rotate-180" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 pb-4 text-sm leading-relaxed text-foreground/65 sm:px-5">
          {item.answer}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
